import {DateRange} from '@blueprintjs/datetime2';
import moment from 'moment';
import {SortingRule} from 'react-table';

import {ApiProjectReport} from 'app/modules/Remote/Feature';

import {makeTableUrlState} from '../tableUrlState';

export const SELECTION_COLUMN_ID = 'selection';
export const TITLE_COLUMN_ID = 'title';
export const TYPE_COLUMN_ID = 'type';
export const PROPERTY_COLUMN_ID = 'property';
export const AUTHOR_COLUMN_ID = 'author';
export const CREATED_COLUMN_ID = 'createdAt';
export const UPDATED_COLUMN_ID = 'updatedAt';

export const SORTABLE_COLUMN_IDS = [
  TITLE_COLUMN_ID,
  PROPERTY_COLUMN_ID,
  AUTHOR_COLUMN_ID,
  CREATED_COLUMN_ID,
  UPDATED_COLUMN_ID,
];

export const DEFAULT_SORT_BY: SortingRule<ApiProjectReport>[] = [
  {id: CREATED_COLUMN_ID, desc: true},
];

// Mirrors MAX_SELECTED_REPORTS in backend/api/blueprints/report.py.
export const MAX_SELECTED_REPORTS = 100;

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_RANGE_SEPARATOR = '..';

function formatDate(date: Date | null): string {
  return date ? moment(date).format(DATE_FORMAT) : '';
}

function parseDate(value: string): Date | null {
  if (!value) {
    return null;
  }
  const parsed = moment(value, DATE_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
}

/** An open-ended range encodes as "2026-01-01.." or "..2026-12-31". */
export function encodeDateRange([start, end]: DateRange): string | undefined {
  if (!start && !end) {
    return undefined;
  }
  return `${formatDate(start)}${DATE_RANGE_SEPARATOR}${formatDate(end)}`;
}

export function decodeDateRange(value: unknown): DateRange {
  if (typeof value !== 'string' || !value) {
    return [null, null];
  }
  const [start = '', end = ''] = value.split(DATE_RANGE_SEPARATOR);
  return [parseDate(start), parseDate(end)];
}

/** Inclusive millisecond bounds covering whole days; open sides are unbounded. */
export function dateRangeBounds([start, end]: DateRange): {startMs: number; endMs: number} {
  return {
    startMs: start ? moment(start).startOf('day').valueOf() : -Infinity,
    endMs: end ? moment(end).endOf('day').valueOf() : Infinity,
  };
}

export function yearDateRange(year: number): DateRange {
  const start = moment({year, month: 0, date: 1});
  return [start.toDate(), start.clone().endOf('year').toDate()];
}

export function isYearDateRange(range: DateRange, year: number): boolean {
  return encodeDateRange(range) === encodeDateRange(yearDateRange(year));
}

/** Years with at least one report, most recent first. */
export function reportYears(reports: Pick<ApiProjectReport, 'createdAt'>[]): number[] {
  const years = new Set(reports.map((r) => moment(r.createdAt).year()));
  return [...years].sort((a, b) => b - a);
}

export interface ReportAuthor {
  id: string;
  name: string;
}

export function reportAuthors(
  reports: Pick<ApiProjectReport, 'createdBy' | 'createdByUser'>[]
): ReportAuthor[] {
  const authorsById = new Map<string, ReportAuthor>();
  reports.forEach((r) => {
    if (!authorsById.has(r.createdBy)) {
      authorsById.set(r.createdBy, {
        id: r.createdBy,
        name: r.createdByUser?.name || r.createdByUser?.email || 'Unknown user',
      });
    }
  });
  return [...authorsById.values()].sort((a, b) => a.name.localeCompare(b.name));
}

const ID_LIST_SEPARATOR = ',';

/** Multi-select filters hold their ids as a comma-separated list. */
export function encodeIdList(ids: string[]): string | undefined {
  return ids.length ? ids.join(ID_LIST_SEPARATOR) : undefined;
}

export function decodeIdList(value: unknown): string[] {
  if (typeof value !== 'string' || !value) {
    return [];
  }
  return value.split(ID_LIST_SEPARATOR).filter((id) => !!id);
}

export function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((other) => other !== id) : [...ids, id];
}

/** Saved reports are the only document type so far. */
export function reportDocumentType(): string {
  return 'Report';
}

/** Document types with at least one document, alphabetical. */
export function reportTypes(reports: ApiProjectReport[]): string[] {
  return [...new Set(reports.map(reportDocumentType))].sort();
}

export interface ReportProperty {
  id: string;
  name: string;
}

/** Properties with at least one document, sorted by name. */
export function reportProperties(reports: Pick<ApiProjectReport, 'feature'>[]): ReportProperty[] {
  const propertiesById = new Map<string, ReportProperty>();
  reports.forEach((r) => {
    if (!propertiesById.has(r.feature.lensId)) {
      propertiesById.set(r.feature.lensId, {
        id: r.feature.lensId,
        name: r.feature.displayName,
      });
    }
  });
  return [...propertiesById.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function reportMatchesSearch(
  report: Pick<ApiProjectReport, 'title' | 'feature'>,
  query: string
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return (
    report.title.toLowerCase().includes(needle) ||
    report.feature.displayName.toLowerCase().includes(needle)
  );
}

// Query-string keys for the reports view's filter and sort state.
const FILTER_URL_KEYS: Record<string, string> = {
  [TITLE_COLUMN_ID]: 'q',
  [TYPE_COLUMN_ID]: 'type',
  [PROPERTY_COLUMN_ID]: 'property',
  [AUTHOR_COLUMN_ID]: 'author',
  [CREATED_COLUMN_ID]: 'created',
};
const MULTI_SELECT_COLUMN_IDS = [PROPERTY_COLUMN_ID, AUTHOR_COLUMN_ID];

export const {tableStateFromSearch, searchWithTableState} = makeTableUrlState<ApiProjectReport>({
  filterUrlKeys: FILTER_URL_KEYS,
  sortableColumnIds: SORTABLE_COLUMN_IDS,
  defaultSortBy: DEFAULT_SORT_BY,
  normalizeFilterValue: (columnId, value) => {
    if (columnId === CREATED_COLUMN_ID) {
      return encodeDateRange(decodeDateRange(value));
    }
    if (MULTI_SELECT_COLUMN_IDS.includes(columnId)) {
      // A list that holds no ids, e.g. "author=,".
      return encodeIdList(decodeIdList(value));
    }
    return value;
  },
});
